import { buttonStyle } from 'components/buttons/buttonStyle';
import { Check as CheckIcon, Sort as SortIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import LabelledData from 'pages/timecards/LabelledData';
import { FormattedMessage } from 'react-intl';
import { InvoiceTimecardAssignment } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { ItemActionButton } from '../../components/ItemActionButton';
import { ITEM_TOTALS_BAR } from '../SectionCard';

export type SortField = 'date' | 'author' | 'billable';
type SortDirection = 'asc' | 'desc';

export interface TimecardSortState {
    field: SortField | null;
    direction: SortDirection;
}

const SORT_FIELDS: { field: SortField; labelKey: string }[] = [
    { field: 'date', labelKey: 'invoices.details.table.date' },
    { field: 'author', labelKey: 'invoices.details.table.author' },
    { field: 'billable', labelKey: 'invoices.details.table.sort-billable' },
];

interface Props {
    currency: string;
    assignment: InvoiceTimecardAssignment;
    sortState: TimecardSortState;
    onSortChange: (state: TimecardSortState) => void;
    isPaymentCurrencyMode?: boolean;
}

const InvoiceTimecardTableTotals: React.FC<Props> = (props) => {
    const { currency, assignment, sortState, onSortChange, isPaymentCurrencyMode } = props;
    const totalTimeUsed = assignment.usedHours || 0;
    const totalTimeBilled = assignment.billedHours || 0;
    const totalAmount = isPaymentCurrencyMode
        ? (assignment.totalAmountPaymentCurrency ?? 0)
        : (assignment.totalAfterAdjustments ?? 0);

    const handleFieldClick = (field: SortField) => {
        onSortChange({ field, direction: sortState.field === field ? sortState.direction : 'asc' });
    };

    const handleDirectionToggle = () => {
        if (sortState.field) {
            onSortChange({ field: sortState.field, direction: sortState.direction === 'asc' ? 'desc' : 'asc' });
        }
    };

    const getButtonLabel = () => {
        if (!sortState.field) return t('invoices.details.table.sort-by');
        const activeField = SORT_FIELDS.find((f) => f.field === sortState.field);
        return `${t('invoices.details.table.sort-by')}: ${activeField ? t(activeField.labelKey) : ''}`;
    };

    return (
        <div className={ITEM_TOTALS_BAR}>
            <div className="flex flex-1 items-center justify-between">
                <div className="flex items-center gap-1">
                    <p className="text-body1">
                        <FormattedMessage id="invoices.details.totals" />
                    </p>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button
                                    {...buttonStyle(
                                        { variant: 'text' },
                                        cn(
                                            'px-2 text-xs font-semibold hover:no-underline',
                                            sortState.field ? 'text-primary' : 'text-fg-secondary'
                                        )
                                    )}
                                />
                            }
                        >
                            {getButtonLabel()}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="min-w-[220px]">
                            {SORT_FIELDS.map(({ field, labelKey }) => (
                                <DropdownMenuItem key={field} onClick={() => handleFieldClick(field)}>
                                    <span className="flex w-5 items-center">
                                        {sortState.field === field && (
                                            <CheckIcon fontSize="small" className="text-primary" />
                                        )}
                                    </span>
                                    <span className="grow">{t(labelKey)}</span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {sortState.field && (
                        <span className="text-primary inline-flex">
                            <ItemActionButton
                                tooltip={
                                    sortState.direction === 'asc'
                                        ? t('invoices.details.table.sort-ascending')
                                        : t('invoices.details.table.sort-descending')
                                }
                                onClick={handleDirectionToggle}
                            >
                                <SortIcon
                                    fontSize="small"
                                    className={cn(sortState.direction === 'desc' && '-scale-y-100')}
                                />
                            </ItemActionButton>
                        </span>
                    )}
                </div>
                <div className="flex flex-row gap-2">
                    <LabelledData label="invoices.details.table.time-used">
                        <span className="font-semibold">
                            <HoursDisplay value={totalTimeUsed} />
                        </span>
                    </LabelledData>
                    <LabelledData label="invoices.details.table.time-billed">
                        <span className="font-semibold">
                            <HoursDisplay value={totalTimeBilled} />
                        </span>
                    </LabelledData>
                    <LabelledData label="invoices.details.table.total-amount">
                        <span className="font-semibold">
                            <MoneyDisplay amount={totalAmount} currency={currency} />
                        </span>
                    </LabelledData>
                </div>
            </div>
        </div>
    );
};

export default InvoiceTimecardTableTotals;
